import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@contexts/LanguageContext';
import { Button } from '@components/common';
import Icon from '@components/Icon/Icon';
import useBorrowForUserOptions from '@hooks/useBorrowForUserOptions';

export const ResourceActions = ({
    resource,
    user,
    resourcePermissions,
    onEdit,
    onDelete,
    className = ''
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const { borrowOptions, hasMultipleOptions } = useBorrowForUserOptions();

    if (!resource) {
        return null;
    }

    // Check if resource requires reservation validation (has reservation managers)
    const requiresValidation = resource.resource_metadata?.some(
        m => m.metadata_key === 'reservation_managers' && m.value === 'true'
    );

    // Fallback for permissions - if not loaded yet, disable all actions
    const permissions = resourcePermissions || {
        canBorrow: false,
        canConsume: false,
        canRefill: false,
        canReserve: false,
        canReturn: false
    };

    // Helper function to check if current user has an active reservation for this resource
    const hasActiveReservation = () => {
        if (!user || !resource.borrowings) {
            return false;
        }

        const now = new Date();
        return resource.borrowings.some(borrowing => {
            // Check if this is an active reservation for the current user
            return (
                borrowing.user_id === user.id &&
                !borrowing.is_deleted &&
                borrowing.borrowed_at === null && // It's a reservation (not borrowed yet)
                borrowing.reserved_from &&
                borrowing.reserved_until &&
                new Date(borrowing.reserved_from) <= now &&
                new Date(borrowing.reserved_until) >= now &&
                !borrowing.returned_at // Not cancelled
            );
        });
    };

    const handleConsume = () => {
        navigate(`/resources/${resource.id}/consume`);
    };

    const handleRefill = () => {
        navigate(`/resources/${resource.id}/refill`);
    };

    const handleBorrow = (targetUserId = null) => {
        const queryParams = new URLSearchParams();
        if (targetUserId) {
            queryParams.set('target_user_id', targetUserId);
        }
        const queryString = queryParams.toString();
        navigate(`/resources/${resource.id}/borrow${queryString ? `?${queryString}` : ''}`);
    };

    const handleBorrowOptionSelect = (option) => {
        handleBorrow(option.targetUserId);
    };

    const handleReturn = () => {
        if (resource.current_borrowing?.id) {
            navigate(`/borrowings/${resource.current_borrowing.id}/return`);
        }
    };

    const handleReserve = () => {
        navigate(`/resources/${resource.id}/reserve`);
    };

    return (
        <div className={`resource-actions ${className}`}>
            {resource.consumable ? (
                // Actions for consumable resources
                <>
                    <Button
                        onClick={handleConsume}
                        disabled={
                            !permissions.canConsume ||
                            !resource.is_available ||
                            (resource.current_quantity ?? 0) <= 0
                        }
                        variant="danger"
                        size="medium"
                    >
                        {t('resourceView.consume')}
                    </Button>
                    <Button
                        onClick={handleRefill}
                        disabled={!permissions.canRefill}
                        variant="success"
                        size="medium"
                    >
                        {t('resourceView.refill')}
                    </Button>
                </>
            ) : (
                // Actions for non-consumable resources
                <>
                    {resource.current_borrowing?.borrowed_at && !resource.current_borrowing?.returned_at ? (
                        // Show return button when resource is borrowed
                        <Button
                            onClick={handleReturn}
                            disabled={!permissions.canReturn}
                            variant="return"
                            size="medium"
                        >
                            {t('resourceView.return')}
                        </Button>
                    ) : requiresValidation ? (
                        // When validation is required, block direct borrow - only allow reservations
                        <Button
                            onClick={() => handleBorrow()}
                            disabled={true}
                            variant="borrow"
                            size="medium"
                            title={t('borrowing.validation.cannotBorrow')}
                        >
                            {t('resourceView.borrow')}
                        </Button>
                    ) : (
                        <Button
                            onClick={() => handleBorrow()}
                            options={borrowOptions}
                            onOptionSelect={handleBorrowOptionSelect}
                            disabled={
                                !permissions.canBorrow ||
                                (!resource.is_available && !hasActiveReservation())
                            }
                            variant="borrow"
                            size="medium"
                        >
                            {t('resourceView.borrow')}
                        </Button>
                    )}
                    <Button
                        onClick={handleReserve}
                        disabled={
                            !permissions.canReserve ||
                            !resource.is_available
                        }
                        variant="reserve"
                        size="medium"
                    >
                        {t('resourceView.reserve')}
                    </Button>
                </>
            )}

            {/* Edit and Delete buttons */}
            {onEdit && permissions.canUpdate && (
                <Button
                    variant="primary"
                    onClick={onEdit}
                    size="medium"
                >
                    <span>{t('common.edit')}</span>
                </Button>
            )}

            {onDelete && permissions.canDelete && (
                <Button
                    variant="danger"
                    onClick={onDelete}
                    size="medium"
                >
                    <span>{t('common.delete')}</span>
                </Button>
            )}
        </div>
    );
};

export default ResourceActions;
